import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import {
  Box,
  Button,
  Chip,
  Divider,
  Radio,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { Dayjs } from "dayjs";
import { memo, useEffect, useMemo, useState } from "react";
import FlexBox from "../../layouts/Flexbox";
import type { SlotCriteriaResult } from "./slotCriteria";
import SlotInvalidCriteriaTooltip from "./SlotInvalidCriteriaTooltip";
import {
  format24hLabelFromMinutes,
  groupSlotsByPeriod,
  SLOT_PERIOD_LABELS,
  SLOT_PERIOD_ORDER,
  type SlotPeriodId,
  type TimeSlotMeta,
  toggleSlotIndex,
} from "./utils";
import {
  getSelectedRadioSx,
  getSelectedSlotChipSx,
  getSlotPillButtonSx,
  getUnselectedRadioSx,
} from "./slotPeriodAccordionPicker.styles";
import "./style.css";

export type SlotPeriodAccordionPickerProps = {
  selectedDate: Dayjs | null;
  disabledDatesNotes?: string[];
  /** When true, hide “Get started” / disabled-dates helper panels (e.g. non-desktop). */
  hideNotes?: boolean;
  /** Invalid slot criteria: tooltip on desktop, bottom drawer on non-desktop. */
  criteriaPresentation?: "tooltip" | "drawer";
  slots: TimeSlotMeta[];
  selectedSlotIndices: number[];
  onChange: (indices: number[]) => void;
  slotCriteriaByIndex: ReadonlyMap<number, SlotCriteriaResult>;
  oppositeUserTimezone: string | null;
  userRole: string;
};

function SlotPeriodAccordionPicker({
  selectedDate,
  disabledDatesNotes,
  hideNotes = false,
  criteriaPresentation = "tooltip",
  slots,
  selectedSlotIndices,
  onChange,
  slotCriteriaByIndex,
  oppositeUserTimezone,
  userRole,
}: SlotPeriodAccordionPickerProps) {
  const theme = useTheme();
  const selectedSet = useMemo(
    () => new Set(selectedSlotIndices),
    [selectedSlotIndices]
  );

  const byPeriod = useMemo(() => groupSlotsByPeriod(slots), [slots]);

  const visiblePeriods = useMemo(
    () => SLOT_PERIOD_ORDER.filter((id) => byPeriod[id].length > 0),
    [byPeriod]
  );

  const [activePeriod, setActivePeriod] = useState<SlotPeriodId | null>(null);
  const [openCriteriaTooltipSlotId, setOpenCriteriaTooltipSlotId] = useState<
    string | null
  >(null);

  useEffect(() => {
    setOpenCriteriaTooltipSlotId(null);
  }, [selectedDate]);

  useEffect(() => {
    if (visiblePeriods.length === 0) {
      setActivePeriod(null);
      return;
    }
    setActivePeriod((current) =>
      current && visiblePeriods.includes(current) ? current : visiblePeriods[0]
    );
  }, [visiblePeriods]);

  const handleToggle = (slotIndex: number) => {
    const selected = selectedSet.has(slotIndex);
    const criteria = slotCriteriaByIndex.get(slotIndex);
    const passes = criteria?.allValid === true;
    if (!selected && !passes) return;
    onChange(toggleSlotIndex(selectedSlotIndices, slotIndex));
  };

  if (!selectedDate) {
    if (hideNotes) {
      return null;
    }
    return (
      <Stack
        width="100%"
        borderRadius={2}
        bgcolor="background.paper"
        p={1.5}
        gap={1}
      >
        <Typography variant="large-helveticaNeue-medium" color="text.primary">
          Get started
        </Typography>

        <Stack gap={0.75} pl={1.5}>
          <FlexBox alignItems="center" gap={1}>
            <TodayOutlinedIcon
              aria-hidden
              fontSize="small"
              color="action"
              className="slotModalIconFlexShrink"
            />
            <Typography variant="body-helveticaNeue" color="text.secondary">
              Choose one or more dates from the calendar.
            </Typography>
          </FlexBox>

          <FlexBox alignItems="center" gap={1}>
            <AccessTimeOutlinedIcon
              aria-hidden
              fontSize="small"
              color="action"
              className="slotModalIconFlexShrink"
            />
            <Typography variant="body-helveticaNeue" color="text.secondary">
              Pick one or more times in{" "}
              <Typography component="span" variant="body-helveticaNeue-bold" color="text.primary">
                Select Time Slot
              </Typography>
              .
            </Typography>
          </FlexBox>

          <FlexBox alignItems="center" gap={1}>
            <ErrorOutlineOutlinedIcon
              aria-hidden
              fontSize="small"
              color="action"
              sx={{ flexShrink: 0 }}
            />
            <Typography variant="body-helveticaNeue" color="text.secondary">
              If a time can’t be selected, click it to see the reason.
            </Typography>
          </FlexBox>
        </Stack>

        {disabledDatesNotes && disabledDatesNotes.length > 0 ? (
          <>
            <Divider flexItem sx={{ borderColor: "divider" }} />
            <Stack gap={1}>
              <FlexBox alignItems="center" gap={1}>
                <BlockOutlinedIcon fontSize="small" color="action" />
                <Typography
                  variant="body-helveticaNeue-bold"
                  color="text.primary"
                >
                  Disabled Dates
                </Typography>
              </FlexBox>
              <Stack gap={0.75} pl={1.5}>
                {disabledDatesNotes.map((note) => (
                  <FlexBox key={note} alignItems="center" gap={1}>
                    <EventBusyOutlinedIcon
                      aria-hidden
                      fontSize="small"
                      color="action"
                      className="slotModalIconFlexShrink"
                    />
                    <Typography
                      variant="body-helveticaNeue"
                      color="text.secondary"
                    >
                      {note}
                    </Typography>
                  </FlexBox>
                ))}
              </Stack>
            </Stack>
          </>
        ) : null}
      </Stack>
    );
  }

  const periodSlots =
    activePeriod !== null ? byPeriod[activePeriod] ?? [] : [];

  return (
    <Stack
      border={1}
      borderColor="divider"
      borderRadius={2}
      bgcolor="background.paper"
      p={1.5}
      gap={1.25}
    >
      <Typography variant="body-helveticaNeue" color="text.secondary">
        {selectedDate.format("ddd, MMM D, YYYY")}
      </Typography>
      <FlexBox
        alignItems="center"
        gap={0.75}
        flexWrap="nowrap"
        className="slotModalPeriodChipScrollRow"
      >
        {visiblePeriods.map((periodId) => {
          const selected = activePeriod === periodId;
          return (
            <Chip
              key={periodId}
              size="small"
              clickable
              onClick={() => setActivePeriod(periodId)}
              label={
                <Typography
                  component="span"
                  variant="xsmall-helveticaNeue-bold"
                  color="inherit"
                >
                  {SLOT_PERIOD_LABELS[periodId]}
                </Typography>
              }
              sx={{
                borderRadius: theme.spacing(1),
                bgcolor: selected ? "primary.main" : "background.paper",
                color: selected ? "primary.contrastText" : "text.secondary",
                border: 1,
                borderColor: selected ? "primary.main" : "divider",
                "&:hover": {
                  bgcolor: selected ? "primary.dark" : "background.paper",
                  borderColor: selected ? "primary.dark" : "primary.light",
                },
              }}
            />
          );
        })}
      </FlexBox>

      <FlexBox
        component="div"
        role="group"
        flexWrap="wrap"
        gap={0.75}
        alignItems="center"
        alignContent="flex-start"
      >
        {periodSlots.map((slot) => {
          const selected = selectedSet.has(slot.index);
          const label24 = format24hLabelFromMinutes(slot.minutesFromMidnight);
          const criteria = slotCriteriaByIndex.get(slot.index);
          const passes = criteria?.allValid === true;

          if (selected) {
            const contrast = passes
              ? "primary.contrastText"
              : "error.contrastText";
            const chip = (
              <Chip
                key={slot.index}
                size="small"
                variant="filled"
                clickable
                onClick={() => handleToggle(slot.index)}
                aria-label={`${label24}, toggle slot`}
                className="slotModalSelectedChip"
                label={
                  <Box
                    component="span"
                    display="inline-flex"
                    alignItems="center"
                    gap={0.35}
                  >
                    <Radio
                      checked
                      size="small"
                      disableRipple
                      tabIndex={-1}
                      sx={getSelectedRadioSx(contrast)}
                    />
                    <Typography
                      component="span"
                      variant="small-mono-medium"
                      fontWeight={600}
                      color="inherit"
                      noWrap
                    >
                      {label24}
                    </Typography>
                  </Box>
                }
                sx={getSelectedSlotChipSx(theme, passes, contrast)}
              />
            );
            if (!passes && criteria && selectedDate) {
              return (
                <SlotInvalidCriteriaTooltip
                  key={slot.index}
                  criteria={criteria}
                  selectedDate={selectedDate}
                  slots={slots}
                  slotIndex={slot.index}
                  userRole={userRole}
                  oppositeUserTimezone={oppositeUserTimezone}
                  tooltipSlotId={String(slot.index)}
                  openTooltipSlotId={openCriteriaTooltipSlotId}
                  setOpenTooltipSlotId={setOpenCriteriaTooltipSlotId}
                  presentation={criteriaPresentation}
                >
                  {chip}
                </SlotInvalidCriteriaTooltip>
              );
            }
            return chip;
          }

          const pillButton = (
            <Button
              key={slot.index}
              type="button"
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => handleToggle(slot.index)}
              aria-disabled={!passes}
              className="slotModalPillButton"
              sx={getSlotPillButtonSx(theme, passes)}
            >
              <FlexBox
                alignItems="center"
                gap={0.35}
                justifyContent="flex-start"
              >
                <Radio
                  checked={false}
                  size="small"
                  color={passes ? "default" : "error"}
                  disableRipple
                  tabIndex={-1}
                  sx={getUnselectedRadioSx(passes)}
                />
                <Typography
                  variant="small-mono-medium"
                  fontWeight={600}
                  color="inherit"
                  noWrap
                >
                  {label24}
                </Typography>
              </FlexBox>
            </Button>
          );

          if (!passes && criteria && selectedDate) {
            return (
              <SlotInvalidCriteriaTooltip
                key={slot.index}
                criteria={criteria}
                selectedDate={selectedDate}
                slots={slots}
                slotIndex={slot.index}
                userRole={userRole}
                oppositeUserTimezone={oppositeUserTimezone}
                tooltipSlotId={String(slot.index)}
                openTooltipSlotId={openCriteriaTooltipSlotId}
                setOpenTooltipSlotId={setOpenCriteriaTooltipSlotId}
                presentation={criteriaPresentation}
              >
                {pillButton}
              </SlotInvalidCriteriaTooltip>
            );
          }

          return pillButton;
        })}
      </FlexBox>
    </Stack>
  );
}

export default memo(SlotPeriodAccordionPicker);
